import React, { useEffect, useState } from "react";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import { plural } from "@/components/common/setUtils";
import {
  ModelItemInstanceJS,
  PhysicalKind,
  physical_kind_from_wire,
} from "target_wasm";
import { CATALOG } from "@/actions/catalog";
import { HoverCardSurface } from "@/components/common/HoverCard";
import { DetailsOverlay } from "@/components/canvas/detailsSide/utils/DetailsOverlay";
import {
  LinkColumnPairs,
  LinkTablesHeader,
} from "@/components/canvas/detailsSide/TableLinks/LinkColumnsDisplay";
import { CategoryChip } from "@/lib/CategoryChip";
import { paletteColors } from "@/lib/chipTextColor";
import { useThemeStore } from "components/main/stores/themeStore";
import { ItemInstanceIcon } from "@/components/common/ItemInstanceIcon";
import { TruncatedText } from "@/components/common/TruncatedText";
import { EditableName } from "@/components/canvas/utils/EditableName";
import { SegmentedControl } from "@/components/common/SegmentedControl";
import { TextInput } from "@/components/common/TextInput";
import { Tooltip } from "@/components/common/Tooltip";
import { TooltipButton } from "@/components/common/TooltipButton";
import { ColumnFormatBadge } from "@/components/canvas/detailsSide/tableDetails/ColumnList/ColumnFormatBadge";
import {
  ColumnLines,
  ColumnType,
  DescriptionMarker,
  LinksMarker,
} from "@/components/canvas/detailsSide/tableDetails/ColumnRow";
import type { ColumnItem } from "@/components/canvas/detailsSide/tableDetails/ColumnList/ColumnListElement";
import { UnitInfoCard } from "@/components/canvas/detailsSide/tableDetails/ColumnList/UnitPicker/UnitInfoCard";
import { useUnitDescription } from "@/components/canvas/detailsSide/tableDetails/ColumnList/UnitPicker/useUnitDescription";
import { useUnitLookup } from "@/components/canvas/detailsSide/tableDetails/ColumnList/UnitPicker/useUnitLookup";
import { UnitPickerView } from "@/components/canvas/detailsSide/tableDetails/ColumnList/UnitPicker/UnitPickerView";
import type { UnitAssignment } from "@/components/canvas/detailsSide/tableDetails/ColumnList/UnitPicker/types";
import { IntentBadges } from "@/components/canvas/detailsSide/tableDetails/ColumnList/IntentBadges";
import {
  deriveScaleLabel,
  scaleFactor,
} from "@/components/canvas/detailsSide/tableDetails/ColumnList/UnitPicker/types";
import type {
  DraftColumn,
  DraftProgress,
  DraftTable,
  TableFeedback,
  Verdict,
} from "@/lib/api/apps/types";
import { Badge } from "@/components/common/Badge";
import { Chip } from "@/components/common/Chip";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import type { ChangeSign as Sign, DraftChange } from "./draft-changes";
import { reviewProse } from "./draft-changes";
import {
  isMarked,
  linkEnds,
  remarkCount,
  shownVerdict,
  type LinkEnd,
} from "./draft-helpers";
import { DraftProgressPanel } from "./draft-progress";
import { semanticLabel } from "./type-label";

// The canvas table diff view's vocabulary (TableDiffViewDetails).
const SIGN: Record<Sign, { color: string; tooltip: string }> = {
  "+": { color: "text-success/60", tooltip: "Added" },
  "-": { color: "text-error/60", tooltip: "Removed" },
  "~": { color: "text-warning/60", tooltip: "Modified" },
};

const SIGN_OF: Record<string, Sign> = { new: "+", changed: "~" };

// The description, link, comment and drop buttons share one shape, so a row's icons sit evenly.
const ROW_ICON = "w-auto! min-w-0! px-1!";

// Level with a column's name on its two-line row; `inline` centres it on a one-line tab.
function SignMark({ sign, inline = false }: { sign?: Sign; inline?: boolean }) {
  if (!sign) return <span className="w-3 flex-none" aria-hidden="true" />;
  return (
    <Tooltip title={SIGN[sign].tooltip}>
      <span
        className={`text-mono-small font-medium w-3 text-center flex-none cursor-default ${inline ? "" : "mt-0.5"} ${SIGN[sign].color}`}
      >
        {sign}
      </span>
    </Tooltip>
  );
}

/** The canvas's type label; `stored` names the storage instead, for a
 *  retype the semantic label would hide (Number → Number). */
/** What QUDT says of a unit, fetched once the hover card opens. */
function UnitHover({ uri }: { uri: string }) {
  const lookup = useUnitLookup(uri);
  const descriptions = useUnitDescription();
  useEffect(() => descriptions.fetch(uri), [descriptions, uri]);
  const entry = descriptions.get(uri);
  if (!lookup.details)
    return (
      <span className="text-fg-muted-regular-small">
        {lookup.loading ? "Reading the unit…" : uri}
      </span>
    );
  return (
    <UnitInfoCard
      unit={lookup.details}
      description={entry?.state === "loaded" ? entry.text : null}
      descriptionLoading={entry?.state === "loading"}
    />
  );
}

/** A column's unit: QUDT's account of it on hover, the picker on click. */
function DraftUnitBadge({
  column,
  pickerOpen,
  onOpen,
}: {
  column: DraftColumn;
  pickerOpen: boolean;
  onOpen?: () => void;
}) {
  return (
    <Tooltip
      variant={column.unit_uri ? "card" : "default"}
      title={
        pickerOpen ? null : column.unit_uri ? (
          <HoverCardSurface>
            <UnitHover uri={column.unit_uri} />
          </HoverCardSurface>
        ) : (
          `Unit: ${column.unit_label ?? column.unit}`
        )
      }
    >
      {/* eslint-disable-next-line no-restricted-syntax -- the canvas's unit badge, clickable here */}
      <button
        type="button"
        disabled={!onOpen}
        aria-label={`Unit: ${column.unit_label ?? column.unit}`}
        aria-expanded={pickerOpen}
        onClick={onOpen}
        className="text-mono-small text-accent enabled:cursor-pointer enabled:hover:underline"
      >
        {column.unit}
      </button>
    </Tooltip>
  );
}

/** A draft column as the canvas's unit picker reads one. */
export function draftColumnItem(column: DraftColumn): ColumnItem {
  return {
    name: column.name,
    dataType: physical_kind_from_wire(column.data_type),
    columnId: column.id ?? column.name,
    lockedType: false,
    unitAssignment: column.unit_uri
      ? {
          stored_unit_uri: column.unit_uri,
          stored_unit_source: column.unit_uri.startsWith("custom:")
            ? "custom"
            : "qudt",
          scale_exponent: column.scale_exponent ?? 0,
          symbol: column.unit,
          label: column.unit_label,
        }
      : undefined,
  };
}

/** A wire type this client does not know shows as written, not as "ND". */
const kindOrUnknown = (dataType: string) => {
  const kind = physical_kind_from_wire(dataType);
  return kind === PhysicalKind.ND ? undefined : kind;
};

function DraftColumnRow({
  column,
  dropped,
  editable,
  note,
  signs,
  linkCount,
  linkOpen,
  onToggle,
  onNote,
  onOpenLink,
  unitOpen,
  onOpenUnit,
  onRename,
}: {
  column: DraftColumn;
  dropped: boolean;
  editable: boolean;
  note: string;
  /** Whether the table's rows carry a change sign, so this one keeps the sign's place. */
  signs: boolean;
  /** Its links, both ways. */
  linkCount: number;
  linkOpen: boolean;
  onToggle: () => void;
  onNote: (note: string) => void;
  onOpenLink: () => void;
  unitOpen: boolean;
  onOpenUnit: () => void;
  /** False keeps the field open: the name is empty or taken. */
  onRename: (name: string) => boolean;
}) {
  const [noting, setNoting] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const isDark = useThemeStore((s) => s.theme === "dark");
  const DropIcon = CATALOG.deleteFromTable.Icon;
  const wasType = column.was && !column.was.startsWith("→") ? column.was : null;
  const stored =
    !!wasType && semanticLabel(wasType) === semanticLabel(column.data_type);
  const noted = note.trim().length > 0;
  // The canvas's scale badge: "k", "M", or 10^n.
  const exponent = column.scale_exponent ?? 0;
  const scale =
    exponent === 0 ? null : (deriveScaleLabel(exponent) ?? `10^${exponent}`);
  const hover = (held: boolean) =>
    held
      ? ""
      : "opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity duration-150";
  return (
    <li className="list-none">
      <div
        className={`group flex items-start gap-2 px-2 py-0.5 rounded-app hover:bg-list-hover ${unitOpen ? "bg-list-hover" : ""}`}
      >
        {signs && (
          <SignMark sign={column.status ? SIGN_OF[column.status] : undefined} />
        )}
        <div
          className="flex-1 min-w-0 flex flex-col"
          style={{ opacity: dropped ? 0.55 : 1 }}
        >
          <ColumnLines
            name={
              <div
                className={`grow min-w-0 pr-2 text-fg-regular ${dropped ? "line-through" : ""}`}
              >
                <EditableName
                  name={column.name}
                  isEditing={renaming}
                  setIsEditing={setRenaming}
                  canEdit={editable && !dropped}
                  update={() => {}}
                  commit={onRename}
                  abort={() => {}}
                  ariaLabel={`Name of ${column.name}`}
                />
              </div>
            }
            actions={
              <>
                {column.description && (
                  <DescriptionMarker title={column.description} />
                )}
                {linkCount > 0 && (
                  <LinksMarker
                    count={linkCount}
                    active={linkOpen}
                    onClick={onOpenLink}
                  />
                )}
                <IntentBadges
                  isNullable={!column.required}
                  isDistinct={column.unique}
                  isPrimaryKeyMember={column.key}
                />
                {editable && (
                  <>
                    <span className={hover(noting || noted)}>
                      <TooltipButton
                        title={noted ? note : "Comment for the agent"}
                        variant="contextual"
                        size="xs"
                        aria-expanded={noting}
                        onClick={() => setNoting((n) => !n)}
                        className={`${ROW_ICON} ${noting || noted ? "text-accent bg-accent/15" : ""}`}
                      >
                        <ChatBubbleOutlineOutlinedIcon />
                      </TooltipButton>
                    </span>
                    <span className={hover(dropped)}>
                      <TooltipButton
                        title={dropped ? "Don't drop column" : "Drop column"}
                        variant="contextual"
                        size="xs"
                        aria-pressed={dropped}
                        onClick={onToggle}
                        className={`${ROW_ICON} ${dropped ? "text-accent bg-accent/15" : ""}`}
                      >
                        <DropIcon />
                      </TooltipButton>
                    </span>
                  </>
                )}
              </>
            }
            kind={kindOrUnknown(column.data_type)}
            typeLabel={column.data_type}
            stored={stored}
            lead={
              column.was && (
                <>
                  <s className="text-mono-small-muted">
                    {wasType ? (
                      <ColumnType
                        kind={kindOrUnknown(wasType)}
                        typeLabel={wasType}
                        stored={stored}
                      />
                    ) : (
                      column.was
                    )}
                  </s>
                  <span className="text-mono-small-muted">→</span>
                </>
              )
            }
            badges={
              <>
                {column.unit && (
                  <DraftUnitBadge
                    column={column}
                    pickerOpen={unitOpen}
                    onOpen={editable && !dropped ? onOpenUnit : undefined}
                  />
                )}
                {scale && (
                  <ColumnFormatBadge
                    label={scale}
                    tooltip={`Scale: ${scale} (×${scaleFactor(column.scale_exponent ?? 0)})`}
                  />
                )}
                {column.options && column.options.length > 0 && (
                  <ColumnFormatBadge
                    label={`${column.options.length} ${column.options.length === 1 ? "category" : "categories"}`}
                    tooltip={
                      // In the palette's order, the colours Apply gives new categories.
                      <span className="flex flex-wrap gap-1">
                        {column.options.map((label, i) => (
                          <CategoryChip
                            key={label}
                            label={label}
                            color={
                              paletteColors(false)[
                                i % paletteColors(false).length
                              ]
                            }
                            isDark={isDark}
                          />
                        ))}
                      </span>
                    }
                  />
                )}
              </>
            }
          />
          {!editable && noted && (
            <span className="text-fg-muted-regular-small">
              You said <q className="text-fg">{note.trim()}</q>
            </span>
          )}
          {editable && noting && (
            <div className="pt-1">
              <TextInput
                value={note}
                onChange={onNote}
                autoFocus
                placeholder={`What should change about ${column.name}?`}
                ariaLabel={`Comment on ${column.name}`}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === "Escape") setNoting(false);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

// One icon per mark, the same in the control that sets it and in Your marks.
const DropMarkIcon = CATALOG.deleteFromTable.Icon;
const MARK_ICON = {
  keep: LockOutlinedIcon,
  change: EditOutlinedIcon,
  drop: DropMarkIcon,
  "drop-column": DropMarkIcon,
  comment: ChatBubbleOutlineOutlinedIcon,
  note: ChatBubbleOutlineOutlinedIcon,
} as const;

const VERDICTS: { value: Verdict; label: string; icon: React.ReactNode }[] = (
  ["keep", "change", "drop"] as const
).map((v) => {
  const Icon = MARK_ICON[v];
  return {
    value: v,
    label: v === "keep" ? "Lock" : v === "change" ? "Change" : "Drop",
    icon: <Icon className="icon-xs" />,
  };
});

export function DraftTableTab({
  table,
  mark = null,
  locked = false,
}: {
  table: DraftTable;
  /** Shown as the mark chips show it: its verdict's icon in place of the table's. */
  mark?: Verdict | null;
  /** Someone has uncommitted changes on it in the canvas. */
  locked?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap">
      {table.status && SIGN_OF[table.status] && (
        <SignMark sign={SIGN_OF[table.status]} inline />
      )}
      {locked ? (
        <LockOutlinedIcon sx={{ fontSize: 14 }} className="text-warning" />
      ) : mark ? (
        <Tooltip title={VERDICT_WORD[mark]}>
          {React.createElement(MARK_ICON[mark], {
            className: "icon-sm shrink-0 text-fg-muted",
            "aria-label": VERDICT_WORD[mark],
          })}
        </Tooltip>
      ) : (
        <ItemInstanceIcon
          instance={ModelItemInstanceJS.Table}
          className="icon-sm shrink-0 text-fg-muted"
        />
      )}
      <span>{table.name}</span>
    </span>
  );
}

export function DraftVerdict({
  table,
  feedback,
  editable,
  onVerdict,
}: {
  table: DraftTable;
  feedback: TableFeedback;
  editable: boolean;
  onVerdict: (v: Verdict) => void;
}) {
  // A locked table: no later round may change it, so it takes no marks.
  if (table.locked)
    return (
      <Tooltip title="You locked this table, so later rounds leave it as it is.">
        <span className="inline-flex items-center gap-1 text-fg-muted-regular-small flex-none cursor-default">
          <LockOutlinedIcon className="icon-sm" />
          Locked
        </span>
      </Tooltip>
    );
  if (editable)
    return (
      <span className="h-6 flex-none">
        <SegmentedControl<Verdict | null>
          options={VERDICTS}
          value={shownVerdict(feedback)}
          autoValue={feedback.verdict ? null : "change"}
          onChange={(v) => v && onVerdict(v)}
          deselectable
        />
      </span>
    );
  return null;
}

export function DraftTableSection({
  table,
  tables,
  feedback,
  editable,
  onToggleColumn,
  onNote,
  onNoteColumn,
  onRenameColumn,
  linkOpenFor,
  onOpenLink,
  unitOpenFor,
  onOpenUnit,
  onCloseUnit,
  onApplyUnit,
  onVerdict,
  notice,
  gone = [],
}: {
  table: DraftTable;
  /** Every table of the version, for the links into this one. */
  tables: DraftTable[];
  feedback: TableFeedback;
  editable: boolean;
  /** Why nothing could be written to this table, at the top of its box. */
  notice?: string;
  /** Notes carried to this version whose column is gone from the model. */
  gone?: string[];
  onToggleColumn: (column: string) => void;
  onNote: (note: string) => void;
  onNoteColumn: (column: string, note: string) => void;
  onRenameColumn: (column: string, name: string) => boolean;
  /** The column whose links are open, if it is one of this table's. */
  linkOpenFor: string | null;
  onOpenLink: (column: DraftColumn) => void;
  /** The column whose unit picker is open, if it is one of this table's. */
  unitOpenFor: string | null;
  onOpenUnit: (column: DraftColumn) => void;
  onCloseUnit: () => void;
  onApplyUnit: (column: DraftColumn, assignment: UnitAssignment | null) => void;
  onVerdict: (verdict: Verdict) => void;
}) {
  const dropping = feedback.verdict === "drop";
  const unitColumn = table.columns.find((c) => c.name === unitOpenFor);
  const signs =
    table.columns.some((c) => c.status) ||
    (table.gone_columns ?? []).length > 0;
  const columnsEditable =
    editable && !table.locked && feedback.verdict !== "keep" && !dropping;
  return (
    <section
      id={`draft-table-${table.id}`}
      className="flex flex-col gap-2 flex-1 min-h-0"
    >
      <div className={`draft-columns${unitColumn ? " with-panel" : ""}`}>
        <div className="flex flex-col gap-2 p-2 rounded-app border border-border bg-bg-subtle overflow-hidden">
          <div className="flex items-center justify-between gap-2 min-h-6 px-2">
            {/* The canvas's columns metric, named. */}
            <span className="inline-flex items-center gap-1 shrink-0 text-fg-muted-regular">
              <TableRowsOutlinedIcon
                className="icon-sm text-fg-muted-medium"
                sx={{ rotate: "90deg" }}
              />
              Columns
              <span className="font-mono tabular-nums">
                {table.columns.length}
              </span>
            </span>
            <span className="flex flex-none">
              <DraftVerdict
                table={table}
                feedback={feedback}
                editable={editable}
                onVerdict={onVerdict}
              />
            </span>
          </div>
          {notice && (
            <div className="px-2 py-2 flex items-start gap-2 rounded-app bg-warning-bg text-warning">
              <LockOutlinedIcon sx={{ fontSize: 16 }} className="mt-0.5" />
              <span className="text-small">{notice}</span>
            </div>
          )}
          {editable && feedback.verdict === "change" ? (
            <div>
              <TextInput
                value={feedback.note}
                onChange={onNote}
                placeholder="What should change? (optional)"
                ariaLabel={`Note on ${table.name}`}
              />
            </div>
          ) : (
            !editable &&
            feedback.note.trim() && (
              <p className="px-2 m-0 text-fg-muted-regular-small">
                You said <q className="text-fg">{feedback.note.trim()}</q>
              </p>
            )
          )}
          {/* A dropped table dims its columns, not the control that can take the drop back. */}
          <ul
            className="flex-1 min-h-0 flex flex-col m-0 p-0 overflow-y-auto scrollbar-overlay-hover"
            style={{ opacity: dropping ? 0.6 : 1 }}
          >
            {table.columns.map((c) => (
              <DraftColumnRow
                key={c.name}
                column={c}
                dropped={feedback.drop_columns.includes(c.name)}
                editable={columnsEditable}
                note={feedback.column_notes?.[c.name] ?? ""}
                signs={signs}
                linkCount={linkEnds(tables, table.id, c.name).length}
                linkOpen={linkOpenFor === c.name}
                onToggle={() => onToggleColumn(c.name)}
                onNote={(n) => onNoteColumn(c.name, n)}
                onOpenLink={() => onOpenLink(c)}
                unitOpen={unitOpenFor === c.name}
                onOpenUnit={() =>
                  unitOpenFor === c.name ? onCloseUnit() : onOpenUnit(c)
                }
                onRename={(name) => onRenameColumn(c.name, name)}
              />
            ))}
            {(table.gone_columns ?? []).map((name) => (
              <li key={`gone-${name}`} className="list-none">
                <div className="flex items-start gap-2 px-2 py-1">
                  <SignMark sign="-" />
                  <span className="text-fg-muted-regular line-through">
                    {name}
                  </span>
                </div>
              </li>
            ))}
          </ul>
          {gone.length > 0 && (
            <div className="px-2 pt-2 border-t border-border flex flex-col gap-0.5 text-fg-muted-regular-small">
              <span>
                {plural(gone.length, "note")} on a column gone from the model:
              </span>
              {gone.map((g) => (
                <span key={g} className="text-fg">
                  {g}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="draft-columns-side">
          {unitColumn && (
            <div className="draft-columns-panel">
              <UnitPickerView
                side="inline"
                tableId={table.id}
                column={draftColumnItem(unitColumn)}
                canEdit={columnsEditable}
                onClose={onCloseUnit}
                onApply={(assignment) => onApplyUnit(unitColumn, assignment)}
              />
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/** A column's links over the graph, as the canvas lists them: both tables named, their linked columns beneath. */
/** A column's links over the graph, as the canvas lists them: both tables named, their linked columns beneath.
 *  The far table's name opens it. */
export function DraftLinkOverlay({
  from,
  ends,
  onGo,
  onHover,
  onClose,
}: {
  from: { table: string; column: string };
  ends: LinkEnd[];
  onGo: (tableId: string) => void;
  onHover: (tableId: string | null) => void;
  onClose: () => void;
}) {
  const icon = (
    <ItemInstanceIcon
      instance={ModelItemInstanceJS.Table}
      className="icon-sm shrink-0 text-fg-muted"
    />
  );
  return (
    // As tall as its links; `resize` is the browser's own corner grip, bounded by the graph's box.
    <div
      className="absolute top-2 right-2 z-10 overflow-hidden"
      style={{
        resize: "both",
        width: "19rem",
        minWidth: "14rem",
        maxWidth: "calc(100% - 1rem)",
        maxHeight: "calc(100% - 1rem)",
      }}
    >
      <DetailsOverlay
        title="Links"
        count={ends.length}
        onClose={onClose}
        side="inline"
      >
        <div className="flex-1 min-h-0 p-2 flex flex-col gap-1 overflow-y-auto">
          {ends.map((end) => (
            <div
              key={`${end.table.id}.${end.column}`}
              className="flex flex-col gap-1 w-full min-w-0 py-1 px-2"
            >
              <LinkTablesHeader
                className="w-full"
                first={
                  <>
                    {icon}
                    <TruncatedText className="text-fg-medium min-w-0">
                      {from.table}
                    </TruncatedText>
                  </>
                }
                second={
                  // eslint-disable-next-line no-restricted-syntax -- a table name that opens its table
                  <button
                    type="button"
                    onClick={() => onGo(end.table.id)}
                    onMouseEnter={() => onHover(end.table.id)}
                    onMouseLeave={() => onHover(null)}
                    className="group flex items-center gap-1 min-w-0 text-fg-medium hover:text-accent cursor-pointer"
                  >
                    <ItemInstanceIcon
                      instance={ModelItemInstanceJS.Table}
                      className="icon-sm shrink-0 text-fg-muted group-hover:text-accent"
                    />
                    <TruncatedText className="min-w-0">
                      {end.table.name}
                    </TruncatedText>
                  </button>
                }
              />
              <LinkColumnPairs
                className="w-full"
                pairs={[{ first: from.column, second: end.column }]}
              />
            </div>
          ))}
        </div>
      </DetailsOverlay>
    </div>
  );
}

const SIGN_CHIP: Record<Sign, string> = {
  "+": "bg-success/15 text-success",
  "-": "bg-error/15 text-error",
  "~": "bg-warning/15 text-warning",
};

/** One change: what was done, and to what. */
function ChangeRow({
  change,
  table,
  onOpen,
}: {
  change: DraftChange;
  table: string;
  onOpen: (tableId: string) => void;
}) {
  // The group names the table; a row names only what is in it.
  const inTable = (path: string) =>
    path.startsWith(`${table}.`) ? path.slice(table.length + 1) : path;
  return (
    <li className="list-none">
      {/* eslint-disable-next-line no-restricted-syntax -- a diff row that opens its table */}
      <button
        type="button"
        disabled={!change.tableId}
        onClick={() => change.tableId && onOpen(change.tableId)}
        className="w-full text-left flex items-start gap-2 px-2 py-1 rounded-app enabled:hover:bg-list-hover enabled:cursor-pointer"
      >
        <Tooltip title={SIGN[change.sign].tooltip}>
          <span
            className={`flex-none size-4 mt-px inline-flex items-center justify-center rounded-app text-mono-small font-medium ${SIGN_CHIP[change.sign]}`}
          >
            {change.sign}
          </span>
        </Tooltip>
        <span className="flex-1 min-w-0 flex flex-col gap-0.5">
          <span className="text-fg-regular">
            <span className="text-fg-muted">{change.verb}</span>{" "}
            <span className="text-fg-medium">{inTable(change.a)}</span>
            {change.b && (
              <>
                {" "}
                <span className="text-fg-muted">{change.mid}</span>{" "}
                <span className="text-fg-medium">{change.b}</span>
              </>
            )}
          </span>
          {change.detail && (
            <span className="text-mono-small-muted break-words">
              {change.detail}
            </span>
          )}
          {change.reason && (
            <span
              className="text-fg-muted-regular-small border-l-2 border-border pl-2 line-clamp-2"
              title={change.reason}
            >
              {change.reason}
            </span>
          )}
        </span>
      </button>
    </li>
  );
}

export function DraftChangeList({
  asked,
  before,
  changes,
  tables,
  onOpen,
}: {
  /** What the round that made this version asked for, above what it changed. */
  asked?: React.ReactNode;
  /** The version this one is compared with, to say when nothing changed. */
  before?: number;
  changes: DraftChange[];
  /** The version's tables and the ones it removed, to name each group. */
  tables: DraftTable[];
  onOpen: (tableId: string) => void;
}) {
  const groups: { key: string; name: string; rows: DraftChange[] }[] = [];
  for (const change of changes) {
    const name =
      tables.find((t) => t.id === change.tableId)?.name ??
      change.a.split(".")[0];
    const key = change.tableId ?? `gone:${name}`;
    const group = groups.find((g) => g.key === key);
    if (group) group.rows.push(change);
    else groups.push({ key, name, rows: [change] });
  }
  return (
    <section className="flex flex-col gap-2 flex-1 min-h-0">
      <div className="flex-1 min-h-0 flex flex-col gap-4 p-2 rounded-app border border-border bg-bg-subtle overflow-y-auto scrollbar-overlay-hover">
        {asked && (
          <div className="flex flex-col gap-1.5 px-2">
            <span className="flex items-center gap-1.5 text-fg-muted-regular-small">
              <ChatBubbleOutlineOutlinedIcon className="icon-sm text-fg-muted" />
              Asked for
            </span>
            {asked}
          </div>
        )}
        {groups.length === 0 && before !== undefined && (
          <p className="m-0 px-2 text-fg-muted-regular">
            No changes from v{before}.
          </p>
        )}
        {groups.map((group) => (
          <div key={group.key} className="flex flex-col gap-1">
            <div className="px-2 flex items-center gap-1.5 text-fg-muted-regular-small">
              <ItemInstanceIcon
                instance={ModelItemInstanceJS.Table}
                className="icon-sm text-fg-muted"
              />
              <span className="text-fg-medium">{group.name}</span>
              <span>{group.rows.length}</span>
            </div>
            <ul className="flex flex-col m-0 p-0">
              {group.rows.map((change, i) => (
                <ChangeRow
                  key={i}
                  change={change}
                  table={group.name}
                  onOpen={onOpen}
                />
              ))}
            </ul>
          </div>
        ))}
      </div>
    </section>
  );
}

/** The marks a round goes out with, as a message's attachments: a chip per marked table, up to `max`, then one
 *  for the rest. A chip opens its table; its close clears that table's marks. */
export function MarkChips({
  marks,
  tables,
  onGo,
  onClear,
  onMore,
  onHover,
  sent = false,
  max = 3,
}: {
  marks: Record<string, TableFeedback>;
  tables: DraftTable[];
  /** Absent, the chips only show what was asked. */
  onGo?: (tableId: string) => void;
  onHover: (tableId: string | null) => void;
  /** Marks an earlier version went with, rather than the next round's. */
  sent?: boolean;
  onClear?: (tableId: string) => void;
  onMore?: () => void;
  max?: number;
}) {
  const marked = tables.filter((t) => isMarked(marks[t.id]));
  const rest = marked.slice(max);
  return (
    <>
      {marked.slice(0, max).map((t) => {
        const f = marks[t.id];
        const verdict = shownVerdict(f);
        const count = remarkCount(f);
        return (
          <Tooltip
            key={t.id}
            title={reviewProse({ note: "", tables: { [t.id]: f } }, tables, {
              sent,
            })}
          >
            <span
              onMouseEnter={() => onHover(t.id)}
              onMouseLeave={() => onHover(null)}
            >
              <Chip
                size="sm"
                label={
                  <span className="inline-flex items-center gap-1">
                    {verdict &&
                      React.createElement(MARK_ICON[verdict], {
                        className: "icon-xs text-fg-muted",
                      })}
                    {t.name}
                  </span>
                }
                count={count || undefined}
                ariaLabel={`${t.name}: ${VERDICT_WORD[verdict ?? "change"]}`}
                onClick={onGo && (() => onGo(t.id))}
                onClose={onClear && (() => onClear(t.id))}
              />
            </span>
          </Tooltip>
        );
      })}
      {rest.length > 0 && (
        <Tooltip title={rest.map((t) => t.name).join(", ")}>
          <span>
            <Chip size="sm" label={`+${rest.length}`} onClick={onMore} />
          </span>
        </Tooltip>
      )}
    </>
  );
}

const VERDICT_WORD: Record<Verdict, string> = {
  change: "To change",
  drop: "To drop",
  keep: "To lock",
};

/** The marks reviewed table by table, as a pull request is reviewed file by file, under a summary of the round:
 *  each table with its verdict's sign, its note, the columns it drops and the comments on its columns. A
 *  table's name opens it and lights it in the graph. */
export function DraftReviewList({
  marks,
  tables,
  title,
  version,
  prose,
  onGo,
  onHover,
  onClose,
}: {
  marks: Record<string, TableFeedback>;
  tables: DraftTable[];
  title: string;
  version: number;
  prose: string;
  onGo: (tableId: string) => void;
  onHover: (tableId: string | null) => void;
  onClose: () => void;
}) {
  const marked = tables.filter((t) => isMarked(marks[t.id]));
  return (
    <section className="flex flex-col gap-2 flex-1 min-h-0">
      <div className="flex-1 min-h-0 flex flex-col gap-4 p-2 rounded-app border border-border bg-bg-subtle overflow-y-auto scrollbar-overlay-hover">
        <div className="flex flex-col gap-1">
          <div className="pl-2 flex items-center gap-1.5 min-h-6">
            <VisibilityOutlinedIcon className="icon-sm text-fg-muted" />
            <span className="text-fg-medium">{title}</span>
            <Badge label={`v${version}`} size="xs" />
            <span className="ml-auto">
              <TooltipButton
                title="Close the review"
                aria-label="Close the review"
                variant="contextual"
                size="xs"
                onClick={onClose}
              >
                <CloseIcon />
              </TooltipButton>
            </span>
          </div>
          <div className="px-2 flex flex-col gap-0.5">
            <span className="text-fg-muted-regular-small">Summary</span>
            <p className="m-0 text-fg-regular">{prose}</p>
          </div>
        </div>
        {marked.map((t) => {
          const f = marks[t.id];
          const verdict = shownVerdict(f);
          const comments = Object.entries(f.column_notes ?? {}).filter(
            ([, n]) => n.trim(),
          );
          const rows: DraftChange[] = [
            ...f.drop_columns.map((c): DraftChange => ({
              sign: "-",
              verb: "Drop column",
              a: c,
              tableId: t.id,
            })),
            ...comments.map(([c, n]): DraftChange => ({
              sign: "~",
              verb: "Comment on",
              a: c,
              reason: n.trim(),
              tableId: t.id,
            })),
          ];
          return (
            <div
              key={t.id}
              className="flex flex-col gap-1"
              onMouseEnter={() => onHover(t.id)}
              onMouseLeave={() => onHover(null)}
            >
              <div className="px-2 flex items-center gap-1.5 text-fg-muted-regular-small">
                <ItemInstanceIcon
                  instance={ModelItemInstanceJS.Table}
                  className="icon-sm text-fg-muted"
                />
                {/* eslint-disable-next-line no-restricted-syntax -- a table's name that opens it */}
                <button
                  type="button"
                  onClick={() => onGo(t.id)}
                  className="text-fg-medium cursor-pointer hover:underline"
                >
                  {t.name}
                </button>
                {verdict && (
                  <Tooltip title={VERDICT_WORD[verdict]}>
                    {React.createElement(MARK_ICON[verdict], {
                      className: "icon-sm text-fg-muted",
                      "aria-label": VERDICT_WORD[verdict],
                    })}
                  </Tooltip>
                )}
              </div>
              {f.note.trim() && (
                <p className="m-0 ml-7 px-2 text-fg-regular border-l-2 border-border">
                  {f.note.trim()}
                </p>
              )}
              {rows.length > 0 && (
                <ul className="flex flex-col m-0 p-0">
                  {rows.map((row, i) => (
                    <ChangeRow
                      key={i}
                      change={row}
                      table={t.name}
                      onOpen={onGo}
                    />
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function DraftingCard({
  progress,
  model,
  version,
  onStop,
  stopping,
}: {
  progress: DraftProgress | null | undefined;
  model: string;
  version: number;
  onStop: () => void;
  stopping: boolean;
}) {
  return (
    <section className="flex-1 min-h-0 flex flex-col gap-3 overflow-y-auto scrollbar-overlay-hover">
      <DraftProgressPanel
        progress={progress}
        model={model}
        version={version}
        onStop={onStop}
        stopping={stopping}
      />
    </section>
  );
}
